import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PropertyEntry {
  name: string;
  address: string;
  bedrooms: string;
  bathrooms: string;
  maxGuests: string;
  description: string;
  amenities: string;
}

export interface OnboardingData {
  properties: PropertyEntry[];
  booking: {
    minNights: string;
    checkInTime: string;
    checkOutTime: string;
    houseRules: string;
    cancellationPolicy: string;
    seasonalPricing: string;
  };
  guestGuide: {
    wifiName: string;
    wifiPassword: string;
    accessInstructions: string;
    localRecommendations: string;
  };
  channels: {
    icalBooking: string;
    icalAirbnb: string;
    otherIcal: string;
  };
  branding: {
    preferredDomain: string;
    brandColors: string;
    languages: string[];
    notes: string;
    // Optional flags — free-form, no schema change. The customer can proceed
    // without brand colours or a logo; the Kanarix team picks these up.
    noBrandColors?: boolean;
    selectedPalette?: string;
    needsLogoDesign?: boolean;
  };
  // "Same for all properties" defaults — pre-filled into each NEW property.
  defaults: {
    checkInTime: string;
    checkOutTime: string;
    houseRules: string;
    cancellationPolicy: string;
  };
  // Done-for-you setup: the owner's existing listing URLs (one per property) for the
  // Kanarix team to copy from. Free-form array, no schema change.
  listingUrls?: string[];
  extraNotes: string;
}

export interface OnboardingProfile {
  status: string;
  legalName: string | null;
  taxId: string | null;
  billingAddress: string | null;
  contactPhone: string | null;
  contactWhatsapp: string | null;
  propertyCount: number | null;
  propertyType: string | null;
  currentWebsite: string | null;
  listingBooking: string | null;
  listingAirbnb: string | null;
  listingVrbo: string | null;
  listingOther: string | null;
  setupMode: 'self' | 'done_for_you' | null;
  data: Partial<OnboardingData>;
  submittedAt: string | null;
}

export interface OnboardingFile {
  id: number;
  category: string;
  label: string | null;
  filename: string;
  content_type: string | null;
  size: number | null;
  created_at: string;
}

export interface AgreementAcceptance {
  doc_key: string;
  doc_version: string;
  doc_title: string;
  accepted_name: string;
  accepted_at: string;
}

export interface AgreementState {
  document: { key: string; version: string; title: string; bodyHtml: string };
  signed: boolean;
  acceptance: AgreementAcceptance | null;
}

export interface OnboardingPayment {
  status: 'unpaid' | 'paid';
  units: number | null;
  billing: 'monthly' | 'yearly' | null;
  siteModel: 'combined' | 'per_property';
  paidAt: string | null;
}

export interface OnboardingResponse {
  profile: OnboardingProfile;
  payment: OnboardingPayment;
  files: OnboardingFile[];
  agreement: AgreementState;
}

// ─── Fetch helper ────────────────────────────────────────────────────────────

const API = '/api/portal';

async function req<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      ...options.headers,
      ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
    },
    credentials: 'same-origin',
  });
  const data = await res.json() as T & { error?: string };
  if (!res.ok) throw new Error(data.error || 'error');
  return data;
}

export const onboardingKeys = {
  root: ['onboarding'] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useOnboarding() {
  return useQuery({
    queryKey: onboardingKeys.root,
    queryFn: () => req<OnboardingResponse>('/onboarding'),
  });
}

export function useSaveOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<{
      legalName: string | null; taxId: string | null; billingAddress: string | null;
      contactPhone: string | null; contactWhatsapp: string | null;
      propertyCount: number | null; propertyType: string | null;
      currentWebsite: string | null; listingBooking: string | null;
      listingAirbnb: string | null; listingVrbo: string | null; listingOther: string | null;
      setupMode: 'self' | 'done_for_you' | null;
      siteModel: 'combined' | 'per_property';
      data: Partial<OnboardingData>;
    }>) => req<{ ok: boolean }>('/onboarding', { method: 'PUT', body: JSON.stringify(payload) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: onboardingKeys.root }),
  });
}

export function useAcceptAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (acceptedName: string) =>
      req<{ ok: boolean; acceptance: AgreementAcceptance }>('/onboarding/agreement', {
        method: 'POST', body: JSON.stringify({ acceptedName }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: onboardingKeys.root }),
  });
}

export function useSubmitOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => req<{ ok: boolean }>('/onboarding/submit', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: onboardingKeys.root }),
  });
}

export function useUploadOnboardingFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, category, label }: { file: File; category: string; label?: string }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      if (label) fd.append('label', label);
      return req<{ ok: boolean; file: OnboardingFile }>('/onboarding/upload', { method: 'POST', body: fd });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: onboardingKeys.root }),
  });
}

export function useOnboardingCheckout() {
  return useMutation({
    mutationFn: ({ units, billing, siteModel }: { units: number; billing: 'monthly' | 'yearly'; siteModel: 'combined' | 'per_property' }) =>
      req<{ url: string }>('/onboarding/checkout', {
        method: 'POST', body: JSON.stringify({ units, billing, siteModel }),
      }),
  });
}

export function useDeleteOnboardingFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/onboarding/files/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: onboardingKeys.root }),
  });
}
